import React, { useState, useCallback, useRef } from 'react';
import { Box, Container } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, NewPageTitleBar } from '@/components';
import FilterShopAllDateRange from '@/components/filters/FilterShopAllDateRange';
import { getMenuData } from '@core/data/menuData';
import AppCard from '@/components/appcard/AppCard';
import { hybridLocService, HybridLocDistributionData } from '@/services/hybridLocService';
import { FCSecurePage, useRegisterRefresh } from '@/components/shared/navigation/securePage.ts';
import WcnApexchart from '@/components/charts/WcnApexchart';
import { DateTime } from 'luxon';
import { FilterRef } from '@features/statistics/types.ts';
import { useNotifications } from '@core/hooks/useNotifications.ts';

interface FilterArgs {
    shopIds: string[];
    start: string;
    end: string;
}

const HybridLocDistributionUsage: FCSecurePage = () => {
    const { t } = useTranslation();
    const [dataLoading, setDataLoading] = useState(false);
    const [series, setSeries] = useState<any[]>([]);
    const [seriesTotal, setSeriesTotal] = useState<any[]>([]);
    const [yAxisMax, setYAxisMax] = useState<number>(5);
    const [xAxisMaxTotal, setXAxisMaxTotal] = useState<number>(1);
    const [lastUpdate, setLastUpdate] = useState<DateTime | undefined>(undefined);
    const filterRef = useRef<FilterRef>(null);
    const { notifyError } = useNotifications();

    const dataLabelsFormatter = useCallback((value: number, opts: any) => {
        const src = opts.w.config.series;
        const { total } = src[opts.seriesIndex]?.data[opts.dataPointIndex] || { total: 0 };
        const percentage = hybridLocService.calculatePercentage(value, total);
        return [value, percentage];
    }, []);

    const loadSeries = (distribution: HybridLocDistributionData[]) => {
        let max = 0;
        const newSeries: any[] = [];
        distribution.forEach((el) => {
            const total = el.countApp + el.countCoin;
            max = total > max ? total : max;
            loadSeriesCount('countApp', el, total, newSeries);
            loadSeriesCount('countCoin', el, total, newSeries);
        });
        setSeries(newSeries);
        setYAxisMax(max > 0 ? max : 5);
    };

    const loadSeriesCount = (
        countName: keyof Pick<HybridLocDistributionData, 'countApp' | 'countCoin'>,
        element: HybridLocDistributionData,
        total: number,
        targetSeries: any[]
    ) => {
        const name = t(countName);
        const idx = targetSeries.findIndex((x) => x.name === name);
        const entry = { x: element.shopName, y: element[countName], total };
        if (idx > -1) targetSeries[idx].data.push(entry);
        else targetSeries.push({ name, data: [entry] });
    };

    const loadTotalSeries = (distribution: HybridLocDistributionData[]) => {
        const totalSeries = hybridLocService.generateTotalSeries(distribution);
        setSeriesTotal(totalSeries);
        const grandTotal =
            hybridLocService.sumArrayProperty(distribution, 'countApp') +
            hybridLocService.sumArrayProperty(distribution, 'countCoin');
        setXAxisMaxTotal(grandTotal);
    };

    const loadData = useCallback(
        async (args: FilterArgs) => {
            setDataLoading(true);
            setSeries([]);
            setSeriesTotal([]);
            try {
                const data = await hybridLocService.loadDistributionUsage(args.start, args.end, args.shopIds);
                loadSeries(data);
                loadTotalSeries(data);
                setLastUpdate(DateTime.now());
            } catch (error) {
                console.error('Failed to load HybridLoc distribution usage data:', error);
                notifyError(t('errorLoadingData'));
            } finally {
                setDataLoading(false);
            }
        },
        [t]
    );

    const handleReload = useCallback(async () => {
        filterRef.current?.refresh();
    }, []);

    useRegisterRefresh(handleReload);

    return (
        <Box sx={{ width: '100%', minHeight: '100%', bgcolor: 'background.default' }}>
            <NewPageTitleBar
                icon={<i className="zmdi zmdi-lock" />}
                title={`${t('hybridLoc')} ${t('appCoinUsage')}`}
                iconSize="18px"
                menus={getMenuData()}
                currentRoute={{ path: '/statistics/hybridloc/appcoin-usage' }}
                lastUpdate={lastUpdate}
                t={t}
            />

            <Container maxWidth={false}>
                <FilterShopAllDateRange
                    ref={filterRef}
                    allShopsSelectedOnStart={true}
                    maxRange={Number.MAX_VALUE}
                    onLoadData={loadData}
                    showDateRangeInfo={false}
                />
                {dataLoading ? (
                    <LoadingSpinner showBackground={false} />
                ) : (
                    <>
                        <AppCard
                            heading={`${t('hybridLoc')} ${t('appCoinUsage')}`}
                            icon="lock"
                            colClasses="flex col-12"
                            customClasses="mb-0"
                            fullBlock={false}
                            footer={false}
                        >
                            <Box id="hl-chart-main" className="hybrid-loc-chart">
                                <WcnApexchart
                                    chartId="hl-chart-main"
                                    type="bar"
                                    series={series}
                                    height={550}
                                    stacked={true}
                                    xAxisType="category"
                                    yAxisMin={0}
                                    yAxisMax={yAxisMax}
                                    dataLabelsEnabled={true}
                                    dataLabelsFormatter={dataLabelsFormatter}
                                    tooltipShared={false}
                                />
                            </Box>
                        </AppCard>
                        <AppCard colClasses="flex col-12" customClasses="mb-0" fullBlock={false} footer={false}>
                            <Box id="hl-chart-total" className="hybrid-loc-chart">
                                <WcnApexchart
                                    chartId="hl-chart-total"
                                    type="bar"
                                    series={seriesTotal}
                                    height={150}
                                    stacked={true}
                                    horizontal={true}
                                    xAxisType="category"
                                    xAxisCategories={[t('total')]}
                                    xAxisMin={0}
                                    xAxisMax={xAxisMaxTotal}
                                    yAxisMin={0}
                                    dataLabelsEnabled={true}
                                    dataLabelsFormatter={dataLabelsFormatter}
                                    tooltipShared={false}
                                />
                            </Box>
                        </AppCard>
                    </>
                )}
            </Container>
        </Box>
    );
};

export default HybridLocDistributionUsage;
